import AppLayout from '../components/layout/AppLayout';
import Card from '../components/common/Card';
import Spinner from '../components/common/Spinner';
import UserRow from '../components/users/UserRow';
import InviteRow from '../components/users/InviteRow';
import InviteForm from '../components/users/InviteForm';
import { useAuth } from '../hooks/useAuth';
import { useOrganizationUsers, usePendingInvites } from '../hooks/useUsers';

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const { data: users, isLoading: usersLoading, error: usersError } = useOrganizationUsers();
  const { data: invites, isLoading: invitesLoading, error: invitesError } = usePendingInvites();

  const canInvite = currentUser?.role === 'admin';
  const canRevoke = currentUser?.role === 'admin';

  const isLoading = usersLoading || invitesLoading;
  const hasError = usersError || invitesError;

  return (
    <AppLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Team Members</h1>
        <p className="text-gray-600 mt-1">Manage users in your organization</p>
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {hasError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-6">
          Failed to load users. Please try again later.
        </div>
      )}

      {!isLoading && !hasError && (
        <div className="space-y-6">
          {/* Invite Form - Admin only */}
          {canInvite && (
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Invite Team Member</h2>
              <InviteForm />
            </Card>
          )}

          {/* Pending Invites */}
          {invites && invites.length > 0 && (
            <Card>
              <div className="px-4 py-3 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  Pending Invites ({invites.length})
                </h2>
              </div>
              <div className="divide-y divide-gray-200">
                {invites.map((invite) => (
                  <InviteRow key={invite.id} invite={invite} canRevoke={canRevoke} />
                ))}
              </div>
            </Card>
          )}

          {/* Users List */}
          <Card>
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Users ({users?.length || 0})
              </h2>
            </div>
            {users && users.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {users.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    isCurrentUser={user.id === currentUser?.id}
                    canRemove={currentUser?.role === 'admin'}
                  />
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                No users found
              </div>
            )}
          </Card>
        </div>
      )}
    </AppLayout>
  );
}
